import { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { SkillPicker } from '@/components/projects/skills/SkillPicker'
import { useConnectors, usePrompts } from '@/hooks'
import {
  MANAGED_PROMPT_PREFIX,
  tryGetManagedPromptName,
  type AgentYamlConfig,
} from '@/lib/agentYaml'
import { useAgentModelCatalog } from '@/hooks'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

const EFFORT_DEFAULT = '__default__'

interface AgentConfigFormProps {
  projectId: string
  config: AgentYamlConfig
  onChange: (config: AgentYamlConfig) => void
  disabled?: boolean
}

function ToolChipsInput({
  tools,
  onChange,
  disabled,
}: {
  tools: string[]
  onChange: (tools: string[]) => void
  disabled: boolean
}) {
  const [draft, setDraft] = useState('')

  const addDraft = () => {
    const tool = draft.trim()
    if (!tool) return
    if (!tools.includes(tool)) onChange([...tools, tool])
    setDraft('')
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {tools.map(tool => (
          <Badge key={tool} variant="secondary" className="gap-1 font-mono text-xs">
            {tool}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove tool ${tool}`}
                onClick={() => onChange(tools.filter(existing => existing !== tool))}
                className="rounded-full opacity-70 hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {tools.length === 0 && (
          <span className="text-xs text-muted-foreground">No tools allow-listed — the agent runs with the defaults of its container.</span>
        )}
      </div>
      {!disabled && (
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              addDraft()
            }
          }}
          placeholder="Type a tool name and press Enter"
          aria-label="Add allowed tool"
          className="max-w-sm"
        />
      )}
      {!disabled && draft.trim() && (
        <p className="text-xs text-amber-600">Press Enter to add "{draft.trim()}" — unconfirmed text is not saved.</p>
      )}
    </div>
  )
}

export function AgentConfigForm({ projectId, config, onChange, disabled = false }: AgentConfigFormProps) {
  const { data: prompts = [] } = usePrompts(projectId)
  const { data: connectors = [] } = useConnectors(projectId)
  const catalog = useAgentModelCatalog('claude-code')

  const isManagedPrompt = tryGetManagedPromptName(config.systemPrompt) !== null
  const managedPromptRef = isManagedPrompt ? config.systemPrompt.trim() : ''

  const promptOptions = useMemo(() => {
    const options = prompts.map(prompt => ({
      value: `${MANAGED_PROMPT_PREFIX}${prompt.internalName}`,
      label: `${prompt.name} · ${prompt.projectPhaseName}`,
    }))
    if (managedPromptRef && !options.some(option => option.value === managedPromptRef)) {
      options.push({ value: managedPromptRef, label: managedPromptRef })
    }
    return options
  }, [prompts, managedPromptRef])

  const modelOptions = useMemo(() => {
    const options = catalog.models.map(model => ({ value: model.id as string, label: model.label }))
    if (config.model && !options.some(option => option.value === config.model)) {
      options.push({ value: config.model, label: config.model })
    }
    return options
  }, [catalog.models, config.model])

  const effortOptions = useMemo(() => {
    const known = catalog.effortsByModel[config.model]
    const allowed: string[] = known
      ? [...known]
      : [...catalog.effortIds]
    if (config.effort && !allowed.includes(config.effort)) allowed.push(config.effort)
    return allowed
  }, [catalog.effortsByModel, catalog.effortIds, config.model, config.effort])

  const mcpOptions: MultiSelectOption[] = useMemo(() => {
    const options = connectors
      .filter(connector => connector.category !== 'Database' && connector.category !== 'CodeQuality' && connector.category !== 'AiProvider')
      .map(connector => ({ value: connector.type as string, label: connector.type as string }))
    for (const ref of config.mcps) {
      if (!options.some(option => option.value === ref)) {
        options.push({ value: ref, label: ref })
      }
    }
    return options
  }, [connectors, config.mcps])

  const update = (partial: Partial<AgentYamlConfig>) => onChange({ ...config, ...partial })

  const updateModel = (model: string) => {
    const allowed: readonly string[] = catalog.effortsByModel[model] ?? []
    const effort = config.effort && allowed.length > 0 && !allowed.includes(config.effort) ? null : config.effort
    update({ model, effort })
  }

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-card p-6" data-testid="agent-config-form">
      <div className="space-y-2">
        <Label className={labelClass} id="agent-system-prompt-label">System prompt <span className="text-red-500" aria-hidden="true">*</span></Label>
        <p className="text-xs text-muted-foreground">
          Reuses the managed Prompt library, or an inline prompt frozen into this agent's versions.
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={isManagedPrompt ? 'default' : 'outline'}
            aria-pressed={isManagedPrompt}
            disabled={disabled || (!isManagedPrompt && promptOptions.length === 0)}
            title={!isManagedPrompt && promptOptions.length === 0 ? 'No managed prompts exist in this project yet' : undefined}
            onClick={() => {
              if (!isManagedPrompt && promptOptions.length > 0) update({ systemPrompt: promptOptions[0].value })
            }}
          >
            Managed prompt
          </Button>
          <Button
            type="button"
            size="sm"
            variant={!isManagedPrompt ? 'default' : 'outline'}
            aria-pressed={!isManagedPrompt}
            disabled={disabled}
            onClick={() => {
              if (isManagedPrompt) update({ systemPrompt: '' })
            }}
          >
            Inline
          </Button>
        </div>
        {isManagedPrompt ? (
          <Select
            value={managedPromptRef || undefined}
            onValueChange={value => update({ systemPrompt: value })}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Managed prompt" aria-required="true" data-testid="managed-prompt-select">
              <SelectValue placeholder="Select a prompt" />
            </SelectTrigger>
            <SelectContent>
              {promptOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Textarea
            value={config.systemPrompt}
            onChange={e => update({ systemPrompt: e.target.value })}
            disabled={disabled}
            rows={8}
            placeholder="You are…"
            aria-label="Inline system prompt"
            aria-required="true"
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label className={labelClass}>Model <span className="text-red-500" aria-hidden="true">*</span></Label>
          <Select
            value={config.model || undefined}
            onValueChange={updateModel}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Model" aria-required="true" data-testid="agent-model-select">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Reasoning effort</Label>
          <Select
            value={config.effort ?? EFFORT_DEFAULT}
            onValueChange={value => update({ effort: value === EFFORT_DEFAULT ? null : value })}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Reasoning effort" data-testid="agent-effort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EFFORT_DEFAULT}>Default</SelectItem>
              {effortOptions.map(effort => (
                <SelectItem key={effort} value={effort} className="capitalize">{effort}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <fieldset className="m-0 space-y-2 border-0 p-0">
        <legend className={labelClass}>Skills</legend>
        <p className="text-xs text-muted-foreground">
          Versioned know-how bundles copied into the container at dispatch. System skills are curated by IDSP; custom skills are yours.
        </p>
        <SkillPicker projectId={projectId} value={config.skills} onChange={skills => update({ skills })} disabled={disabled} />
      </fieldset>

      <div className="space-y-2">
        <Label className={labelClass} htmlFor="agent-mcp-select">MCP Servers</Label>
        <p className="text-xs text-muted-foreground">Each maps to a configured IDSP connector.</p>
        <MultiSelect
          options={mcpOptions}
          value={config.mcps}
          onChange={mcps => update({ mcps })}
          placeholder="Select connectors"
          disabled={disabled}
          className="max-w-md"
          id="agent-mcp-select"
        />
      </div>

      <fieldset className="m-0 space-y-2 border-0 p-0">
        <legend className={labelClass}>Tool allow-list</legend>
        <ToolChipsInput tools={config.tools} onChange={tools => update({ tools })} disabled={disabled} />
      </fieldset>
    </div>
  )
}
